import { Component, inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
    MAT_DIALOG_DATA,
    MatDialogRef,
    MatDialogClose,
} from '@angular/material/dialog';
import { take } from 'rxjs';

import { WorkflowService } from '@services/api/workflow/workflow.service';
import { SelectOption } from '@models/shared.model';
import { FlatButtonComponent } from '@shared/button/flat-button/flat-button.component';
import { AutocompleteFixersComponent } from '@shared/autocomplete/autocomplete-fixers/autocomplete-fixers.component';
import { LoaderComponent } from '@shared/loader/loader.component';
import DATA_QA from '@automation/data-qa.json';

@Component({
    template: `<div class="p-5" [class.disabled]="loading">
            <p class="text-lg mb-3">
                Assign {{ data.discrepancyCount }} {{ discrepancyNoun }}
            </p>
            <p class="text-sm mb-2">Select a person to assign to:</p>
            <app-autocomplete-fixers
                variant="secondary"
                placeholder="Search by name or e-mail"
                [options]="options"
                [inputControl]="analyst" />
            <div class="flex justify-end gap-8 mt-5">
                @if (error) {
                    <small class="color-status-red4">
                        Something went wrong
                    </small>
                }
                <app-flat-button
                    mat-dialog-close
                    [dataQa]="dataQa.assignDiscrepancyDialogCancel">
                    Cancel
                </app-flat-button>
                <app-flat-button
                    variant="cta"
                    (clicked)="assign()"
                    [disabled]="!analyst.value"
                    [dataQa]="dataQa.assignDiscrepancyDialogAssign">
                    Assign
                </app-flat-button>
            </div>
        </div>

        @if (loading) {
            <app-loader type="overlay" position="absolute" />
        }`,
    imports: [
        FlatButtonComponent,
        AutocompleteFixersComponent,
        MatDialogClose,
        LoaderComponent,
    ],
})
export class AssignConfirmDialogComponent implements OnInit {
    readonly dataQa = DATA_QA;
    analyst = new FormControl<string | SelectOption>('');
    loading = false;
    error = false;
    options: SelectOption[] = [];

    readonly data: AssignConfirmDialogData = inject(MAT_DIALOG_DATA);
    private readonly dialogRef = inject(
        MatDialogRef<AssignConfirmDialogComponent>,
    );
    private readonly workflowService = inject(WorkflowService);

    get discrepancyNoun() {
        return this.data.discrepancyCount === 1
            ? 'Discrepancy'
            : 'Discrepancies';
    }

    get selectedAssignee() {
        const selectedValue = this.analyst.value;
        return typeof selectedValue === 'string' ? null : selectedValue;
    }

    ngOnInit(): void {
        this.loadAnalystOptions();
    }

    assign() {
        const selectedAssignee = this.selectedAssignee;

        if (!selectedAssignee) {
            return;
        }

        this.dialogRef.close({
            assign: true,
            assigneeId: Number(selectedAssignee.value),
            assigneeName: selectedAssignee.viewValue,
        });
    }

    private loadAnalystOptions() {
        this.loading = true;
        this.error = false;

        this.workflowService
            .getWorkflowAnalystOptions(this.data.workflowId)
            .pipe(take(1))
            .subscribe({
                next: (options) => (this.options = options),
                error: () => (this.error = true),
                complete: () => (this.loading = false),
            });
    }
}

export interface AssignConfirmDialogData {
    discrepancyCount: number;
    workflowId: number;
}
